/**
 * @fileoverview The messages above the Capabilities table.
 * @description Stacks, in this order, whatever the page has to say besides the matrix itself: the
 * save confirmation, the save error (shown whether or not the page is still in edit mode, so a save
 * refused as stale keeps its explanation), the warning that a save is too large for one request,
 * and the note, with a retry, that refreshing the matrix failed and what is shown may be out of
 * date. The refresh note leaves the table, the draft and the Save button in place.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import CapabilitiesFlash from '@/features/capabilities/components/CapabilitiesFlash'

interface CapabilitiesNoticesProps {
  flash: string
  onDismissFlash: () => void
  saveError: string
  /** Set when one role has more changes than a save may carry. */
  limitWarning: { roleName: string; count: number; max: number } | null
  /** True when the last attempt to refresh the matrix failed. */
  refreshFailed: boolean
  onRetryRefresh: () => void
}

/**
 * @component
 * @param {CapabilitiesNoticesProps} props - The messages to show and their handlers.
 * @param {string} props.flash - The save confirmation, or '' for none.
 * @param {() => void} props.onDismissFlash - Called when the confirmation is dismissed.
 * @param {string} props.saveError - The save error, or '' for none.
 * @param {{ roleName: string; count: number; max: number } | null} props.limitWarning - The role
 * over the per-save limit, its change count and the limit, or null when within it.
 * @param {boolean} props.refreshFailed - Whether refreshing the matrix failed.
 * @param {() => void} props.onRetryRefresh - Tries the refresh again.
 */
function CapabilitiesNotices({
  flash,
  onDismissFlash,
  saveError,
  limitWarning,
  refreshFailed,
  onRetryRefresh,
}: CapabilitiesNoticesProps) {
  const { t } = useTranslation()

  return (
    <>
      {flash && <CapabilitiesFlash message={flash} onDismiss={onDismissFlash} />}

      {saveError && (
        <p role="alert" className="text-[13px] text-danger" data-testid="capabilities-save-error">
          {saveError}
        </p>
      )}

      {limitWarning && (
        <p
          role="alert"
          className="text-[13px] font-medium text-status-warning"
          data-testid="capabilities-limit-warning"
        >
          {t('capabilities.limitWarning', {
            role: limitWarning.roleName,
            count: limitWarning.count,
            max: limitWarning.max,
          })}
        </p>
      )}

      {refreshFailed && (
        <div className="flex flex-wrap items-center gap-3" data-testid="capabilities-refresh-error">
          <p role="alert" className="text-[13px] text-danger">
            {t('capabilities.refreshError')}
          </p>
          <button
            type="button"
            onClick={onRetryRefresh}
            data-testid="capabilities-refresh-retry-button"
            className="cursor-pointer text-[13px] font-semibold text-primary underline"
          >
            {t('capabilities.retry')}
          </button>
        </div>
      )}
    </>
  )
}

export default CapabilitiesNotices
